// eslint-disable-next-line jira/filename-case
import React, { Component, type UIEvent } from 'react';
import { styled, css, type StyledProps } from '@compiled/react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import withFireUiAnalytics from '@jira/platform__analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import Button from '@atlaskit/button/button';
import StarredIcon from '@atlaskit/icon/core/star-starred';
import UnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type AnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { injectIntl, type IntlShape, defineMessage } from '@atlassian/jira-intl';
import type { Props as OwnProps } from '../model/types.tsx';

const addToStarred = defineMessage({
	id: 'platform.ui.favouriting.favourite-button-stateless.add-to-starred',
	defaultMessage: 'Add to Starred',
	description:
		"Tooltip for button to mark item (board, project, issue, filter or dashboard) as favourite. Button is shown in the header of an item's page",
});

const removeFromStarred = defineMessage({
	id: 'platform.ui.favouriting.favourite-button-stateless.remove-from-starred',
	defaultMessage: 'Remove from Starred',
	description:
		'Tooltip for button to unmark item as favourite. This is the same button as the one with "software.main.header.star-button.star-button" text. Current text is shown when item is marked as favourite.',
});

const toggleStarred = defineMessage({
	id: 'platform.ui.favouriting.favourite-button-stateless.toggle-starred',
	defaultMessage: 'Star',
	description:
		'aria-label text as a verb for button to toggle favourite state of an item (board, project, issue, filter or dashboard) without specifying the item name.',
});

const toggleStarredWithItemName = defineMessage({
	id: 'platform.ui.favouriting.favourite-button-stateless.toggle-starred-with-item-name',
	defaultMessage: 'Star {itemName}',
	description:
		'aria-label text as a verb for button to toggle favourite state of the item (board, project, issue, filter or dashboard).',
});

type Props = { intl: IntlShape } & OwnProps;

const SKELETON_TEST_ID = 'favouriting.favorite-button-stateless.skeleton';
const ICON_WRAPPER_TEST_ID_PREFIX = 'favouriting.favorite-button-stateless.icon-wrapper';

// eslint-disable-next-line jira/react/no-class-components
export class FavoriteButtonStateless extends Component<
	Props,
	{
		isAnimating: boolean;
	}
> {
	static defaultProps: {
		isFavorite: undefined;
		favoriteItemName: undefined;
		isShownInList: boolean;
		isSmall: boolean;
		hideTooltip: boolean;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		onClick: (...args: any[]) => void;
		tooltipPosition: string;
		skipAnimation: boolean;
	} = {
		isFavorite: undefined,
		favoriteItemName: undefined,
		isShownInList: false,
		isSmall: false,
		hideTooltip: false,
		onClick: noop,
		tooltipPosition: 'bottom',
		skipAnimation: false,
	};

	state = {
		isAnimating: false,
	};

	componentDidUpdate(prevProps: Props): void {
		if (
			!this.props.skipAnimation &&
			((!prevProps.pending && this.props.pending) ||
				(!prevProps.isFavorite && this.props.isFavorite))
		) {
			this.onAnimationStart();
			this.onSettingAnimationEnd();
		}
	}

	onBtnClickWrapper = (e: UIEvent<HTMLSpanElement>): void => {
		e.stopPropagation(); // should not let this event get outside of the component, to prevent issue when, for example, star button is clicked in an item or row, which goes somewhere else on onClick handler. Cannot put it in onBtnClick, otherwise hideTooltipOnClick in <Tooltip> will not work
		e.preventDefault(); // same for cases when btn is inside a link - not to go to that link (e.g. in Star Drawer)
	};

	onClick = (_: UIEvent<HTMLButtonElement>, analyticsEvent: AnalyticsEvent): void => {
		this.props.onClick(analyticsEvent);
	};

	onAnimationStart(): void {
		this.setState({
			isAnimating: !this.state.isAnimating,
		});
	}

	onSettingAnimationEnd(): void {
		setTimeout(() => {
			this.setState({
				isAnimating: false,
			});
		}, animationTime * 2);
	}

	render(): React.JSX.Element {
		const {
			isFavorite,
			favoriteItemName,
			isShownInList,
			isSmall,
			hideTooltip,
			tooltipPosition,
			pending,
			intl: { formatMessage },
		} = this.props;

		const { isAnimating } = this.state;

		if (isFavorite === undefined) {
			// show skeleton as a space placeholder (to avoid layout recalculations in browser and visible "jump" when we got data)
			return (
				<UFOLoadHold name="favourite-button">
					<FavoriteButtonSkeleton data-testid={SKELETON_TEST_ID} isSmall={isSmall} />
				</UFOLoadHold>
			);
		}

		const iconWrapperTestId = isShownInList
			? `${ICON_WRAPPER_TEST_ID_PREFIX}-list`
			: ICON_WRAPPER_TEST_ID_PREFIX;

		const renderIcon = () => {
			const StarIconComponent = isFavorite ? StarredIcon : UnstarredIcon;

			return (
				<StarIconComponent color="currentColor" label="" size={isSmall ? 'small' : 'medium'} />
			);
		};

		const tooltipContent = isFavorite
			? formatMessage(removeFromStarred)
			: formatMessage(addToStarred);

		const ariaLabel = favoriteItemName
			? formatMessage(toggleStarredWithItemName, { itemName: favoriteItemName })
			: formatMessage(toggleStarred);

		return (
			<FavoriteButtonWrapper
				role="presentation"
				onClick={this.onBtnClickWrapper}
				onKeyPress={this.onBtnClickWrapper}
				data-is-favorite={isFavorite}
			>
				<Tooltip
					content={!hideTooltip && tooltipContent}
					hideTooltipOnClick
					position={tooltipPosition}
				>
					<Button
						appearance={isShownInList ? 'subtle-link' : 'subtle'}
						spacing="none"
						aria-label={ariaLabel}
						aria-pressed={isFavorite}
						onClick={!pending ? this.onClick : noop}
					>
						<FavIconWrapper
							isFavorite={isFavorite}
							isAnimating={isAnimating}
							data-testid={iconWrapperTestId}
							isSmall={isSmall}
						>
							{renderIcon()}
						</FavIconWrapper>
					</Button>
				</Tooltip>
			</FavoriteButtonWrapper>
		);
	}
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const FavoriteButtonStatelessConnected: any = flow(
	withFireUiAnalytics({
		onClick: () => ({
			name: 'star',
		}),
	}),
	injectIntl,
)(FavoriteButtonStateless);
export default FavoriteButtonStatelessConnected;

const animationTime = 500;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FavoriteButtonWrapper: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled.div({
		display: 'inline-block',
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FavoriteIconWrapperBase = styled.span<{
	isSmall?: boolean;
	isFavorite?: boolean;
	isAnimating?: boolean;
}>(
	{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: (props) => (props.isSmall ? '24px' : '32px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) => (props.isSmall ? '24px' : '32px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isAnimating &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'@keyframes energyPop': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'0%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#253858',
					backgroundColor: 'transparent',
					transform: 'scale3d(0.5, 0.5, 0.5)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'20%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#fffae6',
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					backgroundColor: 'rgba(255, 171, 0, 0.2)',
					transform: 'scale3d(1, 1, 1)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'80%': {
					backgroundColor: 'transparent',
					transform: 'scale3d(1.2, 1.2, 1.2)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'100%': {
					backgroundColor: 'transparent',
					color: 'currentColor',
					transform: 'none',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			svg: {
				animation: `energyPop ${animationTime / 1000}s ease`,
				animationFillMode: 'forwards',
				willChange: 'transform',
			},
		}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FavIconWrapper = styled(FavoriteIconWrapperBase)<{
	isSmall?: boolean;
	isFavorite?: boolean;
	isAnimating?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.isFavorite ? token('color.icon.accent.yellow') : token('color.icon.subtle'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) =>
			props.isFavorite ? token('color.background.accent.yellow.bolder') : token('color.icon'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FavoriteButtonSkeleton: React.ComponentType<
	{
		isSmall?: boolean;
	} & React.ClassAttributes<HTMLDivElement> &
		React.HTMLAttributes<HTMLDivElement> &
		StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled.div<{ isSmall?: boolean }>({
		display: 'flex',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: (props) => (props.isSmall ? '24px' : '32px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) => (props.isSmall ? '24px' : '32px'),
	});
